/// <amd-module name="Build/Queue-Panel/DemandPanel" />
import * as React from "react";
import { IDemandData, DemandCondition } from "Types";

import { FormInputLabel } from "Build/Queue-Panel/FormInputLabel";
import * as Resources from "Build/Queue-Panel/Resources";

import { FormItem } from "VSSUI/FormItem";
import { PickListDropdown } from "VSSUI/PickList";
import { Panel } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";

export interface IDemandPanelState {
    demand: IDemandData;
}

export interface IDemandPanelProps {
    demand: IDemandData;
    isNewDemand: boolean; // if the panel is used for creating a new demand or updating an existing one
    onDemandAdded?(demand: IDemandData): void;
    onDemandUpdated?(demand: IDemandData): void;
    onDismiss(): void;
}

export class DemandPanel extends React.Component<IDemandPanelProps, IDemandPanelState> {

    constructor(props: IDemandPanelProps) {
        super(props);

        this.state = {
            demand: this.props.demand
        };
    }

    public render(): JSX.Element {
        const title = this.props.isNewDemand ? Resources.AddDemandPanelTitle : Resources.UpdateDemandPanelTitle;
        const primaryButtonText = this.props.isNewDemand ? Resources.CreateButtonText : Resources.UpdateButtonText;
        const disablePrimaryButton = !this._isValidName() || !this._isValidValue();
        const defaultActiveElement = this.props.isNewDemand ? ".variable-panel-demand-name" : ".variable-panel-demand-condition";

        return (
            <Panel
                backButtonProps={{
                    subtle: true,
                    iconProps: { iconName: "Back" },
                    onClick: this.props.onDismiss,
                    ariaLabel: Resources.Back
                }}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: title }}
                className="variable-panel"
                defaultActiveElement={defaultActiveElement}
                footerButtonProps={[
                {
                    text: Resources.CancelButtonText, 
                    onClick: this.props.onDismiss
                },
                {
                    text: primaryButtonText, 
                    primary: true, 
                    onClick: this._clickOk,
                    disabled: disablePrimaryButton
                }]}
            >
                <DemandPanelContent
                    demand={this.state.demand}
                    isNewDemand={this.props.isNewDemand}
                    onNameChanged={this._onNameChanged}
                    onConditionChanged={this._onConditionChanged}
                    onValueChanged={this._onValueChanged}
                    onKeyDown={this._onKeyDown}
                />
            </Panel>
        );
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // If we press enter from within the panel this will click the create button.
        const disablePrimaryButton = !this._isValidName() || !this._isValidValue();
        if (event.key === "Enter" && !event.defaultPrevented && !disablePrimaryButton) {
            this._clickOk();
            event.preventDefault();
        }
    }

    private _onNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> , newValue: string) => {
        this.state.demand.name = newValue;
        this.setState({
            demand: this.state.demand
        });
    }

    private _onValueChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
        this.state.demand.value = newValue;
        this.setState({
            demand: this.state.demand
        });
    }

    private _onConditionChanged = (newValue: DemandCondition) => {
        this.state.demand.condition = newValue;
        this.setState({
            demand: this.state.demand
        });
    }

    private _isValidName = (): boolean => {
        const item = this.state.demand;
        if (!item.name || item.name.trim() === "")
        {
            return false;
        }

        return true;
    }

    private _isValidValue = (): boolean => {
        const item = this.state.demand;
        if (item.condition === DemandCondition.Equals && (!item.value || item.value.trim() === ""))
        {
            return false;
        }

        return true;
    }

    private _clickOk = () => {
        if (this.props.isNewDemand && this.props.onDemandAdded)
        {
            this.props.onDemandAdded(this.state.demand);
        }
        else if (!this.props.isNewDemand && this.props.onDemandUpdated)
        {
            this.props.onDemandUpdated(this.state.demand);
        }
        
        this.props.onDismiss();
    }
}

interface IDemandPanelContentProps {
    demand: IDemandData;
    isNewDemand: boolean; // if the panel is used for creating a new demand or updating an existing one
    onNameChanged(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> , newValue: string): void;
    onValueChanged(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void;
    onConditionChanged(newValue: DemandCondition): void;
    onKeyDown(ev: React.KeyboardEvent<HTMLDivElement>): void;
}

class DemandPanelContent extends React.Component<IDemandPanelContentProps> {
    private _conditionDropDownRef: PickListDropdown;
    private _nameTextFieldRef: TextField;

    public render(): JSX.Element {
        return (
            <div className="variable-panel-content flex-grow" onKeyDown={this.props.onKeyDown}>
                {this._getDemandNameControl()}
                {this._getDemandConditionControl()}
                {this.props.demand.condition === DemandCondition.Equals && this._getDemandValueControl()}
            </div>
        );
    }

    public componentDidMount() {
        if (this.props.isNewDemand)
        {
            this._nameTextFieldRef && this._nameTextFieldRef.focus();
            this._nameTextFieldRef && this._nameTextFieldRef.select();
        }
        else {
            this._conditionDropDownRef && this._conditionDropDownRef.focus();
        }
    }

    private _getDemandNameControl = (): JSX.Element => {
        return ( 
            <FormItem
                className="variable-panel-demand-name flex-grow margin-bottom-16"
                label={<FormInputLabel primaryLabel={Resources.NameFieldLabel} secondaryLabel={Resources.RequiredLabel} />}
            >
                <TextField
                    ariaLabel={Resources.NameFieldLabel}
                    value={this.props.demand.name} 
                    className="primary-text"
                    ref={element => {this._nameTextFieldRef = element!}}
                    onChange={this.props.onNameChanged} 
                    disabled={!this.props.isNewDemand}
                    required={true}
                />
            </FormItem>
        );
    }

    private _getDemandConditionControl = (): JSX.Element => {
        return (
            <div className="variable-condition-container flex-grow margin-bottom-16">
                <label className="flex-grow flex-row margin-bottom-8">{Resources.ConditionFieldLabel}</label>
                <PickListDropdown
                    selectOnFocus={true}
                    ariaLabelFormat={Resources.ConditionFieldLabel}
                    selectedItems={this._getSelectedCondition()}
                    className="variable-panel-demand-condition flex-grow primary-text"
                    ref={element => {this._conditionDropDownRef = element!}}
                    getPickListItems={this._getConditions}
                    getListItem={(item: IDemandConditionData) => {
                        return {
                            key: item && item.key ? item.key.toString() : "",
                            name: item ? item.name : ""
                        };
                    }}
                    onSelectionChanged={(selection) => this.props.onConditionChanged(selection.selectedItems[0].key)}
                />
            </div>
        );
    }

    private _getDemandValueControl = (): JSX.Element => {
        return (
            <FormItem
                className="flex-grow margin-bottom-16"
                label={<FormInputLabel primaryLabel={Resources.ValueFieldLabel} secondaryLabel={Resources.RequiredLabel} />}
            >
                <TextField 
                    value={this.props.demand.value} 
                    className="primary-text"
                    onChange={this.props.onValueChanged}
                    required={true}
                />
            </FormItem>
        );
    }

    private _getSelectedCondition = () => {
        if (this.props.demand.condition === DemandCondition.Equals)
        {
            return [{key: this.props.demand.condition, name: Resources.EqualsCondition}];
        }

        return [{key: this.props.demand.condition, name: Resources.ExistsCondition}];
    }

    private _getConditions = () =>
    {
        return [{key: DemandCondition.Equals, name: Resources.EqualsCondition},
                {key: DemandCondition.Exists, name: Resources.ExistsCondition}];
    }
}

interface IDemandConditionData {
    key : number;
    name: string;
}
